import React, { useContext, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import type { WithFlagsProps } from '@confluence/flags';
import { withFlags } from '@confluence/flags';
import { SPAViewContext } from '@confluence/spa-view-context';
import { AccessStatus, useSessionData } from '@confluence/session-data';
import {
	confluenceSessionStorageInstance as sessionStorage,
	keys as storageKeys,
} from '@confluence/storage-manager';
import { getNav4GAPhase, useGetNav4OptIn, useIsNav4Enabled } from '@confluence/nav4-enabled';

/**
 * There is a possible scenario where individual admins have nav4 toggled on/off,
 * and a different admin decides to turn it on sitewide for all users,
 * which could override an admin’s setting.
 * This flag is used to notify other site admins that there may be a misalignment with what they’re seeing vs the other users on the tenant.
 */
export const MisalignedNavVersionsFlag = withFlags(({ flags }: WithFlagsProps) => {
	const hasShownInfoFlag = sessionStorage.getItem(storageKeys.NAV4_MISALIGNED_NAV_VERSIONS_FLAG);

	const { isSiteAdmin } = useContext(SPAViewContext);
	const { accessStatus } = useSessionData();
	const isAdmin = accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS;

	const { nav4AdminOptedIn: isSiteWideNav4Enabled } = useGetNav4OptIn();
	const isNav4DisplayedToUser = useIsNav4Enabled();
	const currentGAPhase = getNav4GAPhase();

	/**
	 * The user is an admin capable of admin-only opt-out
	 * We are in GA phase 1
	 * Nav 4 is opted-in at the site level
	 * The user is seeing nav 3 due to prior personal preference update
	 */
	const navVersionsAreMisaligned =
		(isSiteAdmin || isAdmin) &&
		isSiteWideNav4Enabled &&
		currentGAPhase === 1 &&
		!isNav4DisplayedToUser;

	useEffect(() => {
		if (!hasShownInfoFlag && navVersionsAreMisaligned) {
			void flags.showInfoFlag({
				title: <FormattedMessage {...i18n.misalignedNavVersionsFlagTitle} />,
				description: <FormattedMessage {...i18n.misalignedNavVersionsFlagDescription} />,
				actions: [
					{
						content: <FormattedMessage {...i18n.misalignedNavVersionsFlagLink} />,
						href: '/wiki/admin/newfeatures',
					},
				],
				isAutoDismiss: false,
			});
			sessionStorage.setItem(storageKeys.NAV4_MISALIGNED_NAV_VERSIONS_FLAG, 'true');
		}
	}, [hasShownInfoFlag, navVersionsAreMisaligned, flags]);

	return null;
});

export const i18n = defineMessages({
	misalignedNavVersionsFlagTitle: {
		id: 'nav-versions-misaligned.flag',
		defaultMessage: 'New navigation is turned on for everyone',
		description: 'Title to be shown when the misaligned nav versions flag is showing.',
	},
	misalignedNavVersionsFlagDescription: {
		id: 'nav-versions-misaligned.flag.description',
		defaultMessage:
			'You are still on the old navigation because you previously opted out. You can update your navigation preferences in settings.',
		description: 'Description to be shown when the misaligned nav versions flag is showing.',
	},
	misalignedNavVersionsFlagLink: {
		id: 'nav-versions-misaligned.flag.link',
		defaultMessage: 'Go to settings',
		description: 'Link to be shown when the misaligned nav versions flag is showing.',
	},
});
